import { useAnimations } from '@/hooks/useAnimation';
import { searchAssistantUIStore } from '@/stores/searchAssistantStore';
import { Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CHAT_WINDOW_SIZES } from '../constants';
import { ChatSideBarList } from './ChatSideBarList';

const styles = {
  scrollableContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 9,
    transform: 'translate3d(-102.5%, 0, 0)',
    scrollSnapType: 'y mandatory',
    scrollBehavior: 'smooth',
    overflowY: 'auto',
    width: CHAT_WINDOW_SIZES.SIDEBAR_WIDTH,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0.25rem',
    backgroundColor: (theme) => `${theme.palette.baseColors.deepSeaNavy.c100}fa`,
  },
};

const ANIMATION_CONSTANTS = {
  DURATION: 300,
  EASING: 'ease-out',
};

const animationOptions = {
  duration: ANIMATION_CONSTANTS.DURATION,
  easing: ANIMATION_CONSTANTS.EASING,
  fill: 'both',
};

const ANIMATION_STATES = {
  FINISHED: 'finished',
};

const SIDEBAR_MARGIN = CHAT_WINDOW_SIZES.SIDEBAR_WIDTH * 0.025;
const SIDEBAR_WIDTH_WITH_MARGIN = CHAT_WINDOW_SIZES.SIDEBAR_WIDTH + SIDEBAR_MARGIN;

const ChatSidebar = () => {
  const isSidebarOpen = searchAssistantUIStore.use.isSidebarOpen();
  const chatPosition = searchAssistantUIStore.use.position();
  const [shouldRender, setShouldRender] = useState(isSidebarOpen);
  const [posX, setPosX] = useState(SIDEBAR_WIDTH_WITH_MARGIN);
  const sidebarSide = useRef(true);

  const { ref, animate } = useAnimations({
    autoPlay: false,
    onFinish: ({ playState }) => {
      if (!isSidebarOpen && playState === ANIMATION_STATES.FINISHED) {
        setShouldRender(isSidebarOpen);
      }
    },
  });

  const slideOutKeyframes = useMemo(
    () => [
      { transform: 'translate3d(0, 0, 0)', opacity: 0.75 },
      { transform: `translate3d(${posX}px, 0, 0)`, opacity: 1 },
    ],
    [posX],
  );

  useEffect(() => {
    if (chatPosition.x > SIDEBAR_WIDTH_WITH_MARGIN) {
      setPosX(SIDEBAR_WIDTH_WITH_MARGIN * -1);
      return;
    }

    setPosX(CHAT_WINDOW_SIZES.WIDTH + SIDEBAR_MARGIN);
  }, [chatPosition.x]);

  useEffect(() => {
    if (isSidebarOpen && !shouldRender) {
      setShouldRender(isSidebarOpen);
      return;
    }

    const slideInKeyframes = [{ opacity: 0.5 }, { transform: 'translate3d(0, 0, 0)', opacity: 0 }];

    if (animate) {
      animate({
        keyframes: isSidebarOpen ? slideOutKeyframes : slideInKeyframes,
        animationOptions,
      });
    }
  }, [isSidebarOpen, animate, shouldRender, slideOutKeyframes]);

  useEffect(() => {
    if (!isSidebarOpen) {
      return;
    }

    const isLeftSide = chatPosition.x < SIDEBAR_WIDTH_WITH_MARGIN;

    if (sidebarSide.current === isLeftSide) {
      return;
    }

    sidebarSide.current = isLeftSide;

    if (animate) {
      animate({
        keyframes: slideOutKeyframes,
        animationOptions,
      });
    }
  }, [chatPosition.x, animate, isSidebarOpen, slideOutKeyframes]);

  if (!shouldRender) {
    return null;
  }

  return (
    <Box ref={ref} as="aside" sx={styles.scrollableContainer} className="chat-sidebar">
      <ChatSideBarList />
    </Box>
  );
};

export { ChatSidebar };
